import { Box, Button, Typography } from '@mui/material';
import { Mail, OpenInNew } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { gql } from '../../__generated__';
import { useConfig } from '../../components/Configuration';
import { formatDate } from '../../Formatters';
import { TintedIcon } from './TintedIcon';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Notification {
  id: string;
  subject: string;
  sentAt: string;
}

const USER_NOTIFICATIONS = gql(`
  query UserNotificationsList($userId: Int!, $offset: Int, $limit: Int) {
    queryResult: getUserNotifications(userId: $userId, offset: $offset, limit: $limit) {
      totalCount
      notifications {
        id
        subject
        sentAt
      }
    }
  }
`);

interface Props {
  userId: number;
}

export function NotificationsPanel({ userId }: Props) {
  const config = useConfig();
  const pollInterval = config?.defaultPollingInterval ?? 0;
  const rootUrl = '/server/api';

  const { loading, error, data, fetchMore } = useQuery<{
    queryResult: { totalCount: number; notifications: Notification[] };
  }>(USER_NOTIFICATIONS, {
    variables: { userId, offset: 0, limit: 5 },
    pollInterval,
  });

  if (error) return <Box sx={{ p: 3 }}><span>Feil! {error.message}</span></Box>;
  if (loading && !data) return <Box sx={{ p: 3 }}><LoadingSpinner /></Box>;

  const list = data?.queryResult.notifications ?? [];
  const moreToLoad = data ? list.length < data.queryResult.totalCount : false;

  if (list.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="caption" color="textDisabled">Ingen e-poster</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {list.map((n, i) => (
        <Box
          key={n.id}
          component="a"
          href={`${rootUrl}/notification/user/${userId}?notificationId=${n.id}`}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.75,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 1.75 },
            borderTop: i === 0 ? 0 : 1,
            borderColor: 'divider',
            color: 'text.primary',
            textDecoration: 'none',
            '&:hover': { bgcolor: 'action.hover' },
          }}>
          <TintedIcon>
            <Mail fontSize="small" />
          </TintedIcon>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 'fontWeightMedium' }}
              dangerouslySetInnerHTML={{ __html: n.subject }}
            />
            <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25 }}>
              {formatDate(n.sentAt)}
            </Typography>
          </Box>
          <OpenInNew fontSize="small" color="disabled" />
        </Box>
      ))}

      {moreToLoad && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.25, sm: 1.5 }, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Button
            size="small"
            disabled={loading}
            onClick={() =>
              fetchMore({
                variables: { offset: list.length },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult?.queryResult?.notifications?.length) return prev;
                  return {
                    ...prev,
                    queryResult: {
                      ...prev.queryResult,
                      notifications: [...prev.queryResult.notifications, ...fetchMoreResult.queryResult.notifications],
                    },
                  };
                },
              })
            }>
            Last flere
          </Button>
        </Box>
      )}
    </Box>
  );
}
